'use client';

import type { ButtonProps, TextProps } from '@chakra-ui/react';
import { Button, Pagination as ChakraPagination, IconButton, Text, createContext, usePaginationContext } from '@chakra-ui/react';
import { forwardRef, useMemo } from 'react';
import { LinkButton } from './link-button';
import { ChevronLeft, ChevronRight, Ellipsis } from 'lucide-react';

interface ButtonVariantMap {
    current: ButtonProps['variant'];
    default: ButtonProps['variant'];
    ellipsis: ButtonProps['variant'];
}

type PaginationVariant = 'outline' | 'solid' | 'subtle';

interface ButtonVariantContext {
    size: ButtonProps['size'];
    variantMap: ButtonVariantMap;
    getHref?: (page: number) => string;
}

const [RootPropsProvider, useRootProps] = createContext<ButtonVariantContext>({
    name: 'RootPropsProvider',
});

export interface PaginationRootProps extends Omit<ChakraPagination.RootProps, 'type'> {
    size?: ButtonProps['size'];
    variant?: PaginationVariant;
    getHref?: (page: number) => string;
}

const variantMap: Record<PaginationVariant, ButtonVariantMap> = {
    outline: { default: 'ghost', ellipsis: 'plain', current: 'outline' },
    solid: { default: 'outline', ellipsis: 'outline', current: 'solid' },
    subtle: { default: 'ghost', ellipsis: 'plain', current: 'subtle' },
};

export const PaginationRoot = forwardRef<HTMLDivElement, PaginationRootProps>(function PaginationRoot(props, ref) {
    const { size = 'sm', variant = 'outline', getHref, ...rest } = props;
    return (
        <RootPropsProvider value={{ size, variantMap: variantMap[variant], getHref }}>
            <ChakraPagination.Root ref={ref} type={getHref ? 'link' : 'button'} {...rest} />
        </RootPropsProvider>
    );
});

export const PaginationEllipsis = forwardRef<HTMLDivElement, ChakraPagination.EllipsisProps>(function PaginationEllipsis(props, ref) {
    const { size, variantMap } = useRootProps();
    return (
        <ChakraPagination.Ellipsis ref={ref} {...props} asChild>
            <Button as="span" variant={variantMap.ellipsis} size={size}>
                <Ellipsis />
            </Button>
        </ChakraPagination.Ellipsis>
    );
});

export const PaginationItem = forwardRef<HTMLButtonElement, ChakraPagination.ItemProps>(function PaginationItem(props, ref) {
    const { page } = usePaginationContext();
    const { size, variantMap, getHref } = useRootProps();

    const current = page === props.value;
    const variant = current ? variantMap.current : variantMap.default;

    if (getHref) {
        return (
            <LinkButton to={getHref(props.value)} variant={variant} size={size}>
                {props.value}
            </LinkButton>
        );
    }

    return (
        <ChakraPagination.Item ref={ref} {...props} asChild>
            <Button variant={variant} size={size}>
                {props.value}
            </Button>
        </ChakraPagination.Item>
    );
});

export const PaginationPrevTrigger = forwardRef<HTMLButtonElement, ChakraPagination.PrevTriggerProps>(
    function PaginationPrevTrigger(props, ref) {
        const { size, variantMap, getHref } = useRootProps();
        const { previousPage } = usePaginationContext();

        if (getHref) {
            return (
                <LinkButton to={previousPage != null ? getHref(previousPage) : ''} variant={variantMap.default} size={size}>
                    <ChevronLeft />
                </LinkButton>
            );
        }

        return (
            <ChakraPagination.PrevTrigger ref={ref} asChild {...props}>
                <IconButton variant={variantMap.default} size={size}>
                    <ChevronLeft />
                </IconButton>
            </ChakraPagination.PrevTrigger>
        );
    },
);

export const PaginationNextTrigger = forwardRef<HTMLButtonElement, ChakraPagination.NextTriggerProps>(
    function PaginationNextTrigger(props, ref) {
        const { size, variantMap, getHref } = useRootProps();
        const { nextPage } = usePaginationContext();

        if (getHref) {
            return (
                <LinkButton to={nextPage != null ? getHref(nextPage) : ''} variant={variantMap.default} size={size}>
                    <ChevronRight />
                </LinkButton>
            );
        }

        return (
            <ChakraPagination.NextTrigger ref={ref} asChild {...props}>
                <IconButton variant={variantMap.default} size={size}>
                    <ChevronRight />
                </IconButton>
            </ChakraPagination.NextTrigger>
        );
    },
);

export const PaginationItems = (props: React.HTMLAttributes<HTMLElement>) => {
    return (
        <ChakraPagination.Context>
            {({ pages }) =>
                pages.map((page, index) => {
                    return page.type === 'ellipsis' ? (
                        <PaginationEllipsis key={index} index={index} {...props} />
                    ) : (
                        <PaginationItem key={index} type="page" value={page.value} {...props} />
                    );
                })
            }
        </ChakraPagination.Context>
    );
};

interface PageTextProps extends TextProps {
    format?: 'short' | 'compact' | 'long' | 'page';
}

export const PaginationPageText = forwardRef<HTMLParagraphElement, PageTextProps>(function PaginationPageText(props, ref) {
    const { format = 'compact', ...rest } = props;
    const { page, pages, pageRange, count } = usePaginationContext();
    const content = useMemo(() => {
        if (format === 'short') {
            return `${page} / ${pages.length}`;
        }
        if (format === 'compact') {
            return `${page} of ${pages.length}`;
        }
        if (format === 'page') {
            return page;
        }
        return `${pageRange.start + 1} - ${pageRange.end} of ${count}`;
    }, [format, page, pages.length, pageRange, count]);

    return (
        <Text fontWeight="medium" ref={ref} {...rest}>
            {content}
        </Text>
    );
});
